import React from "react";

/**
 * Shared partner → colour legend. When `visibleIds` / `onToggle` are provided,
 * chips are clickable so users can show/hide partners across all charts
 * (especially useful in date-wise multi-partner mode).
 */
const PartnerLegend = ({ partners = [], colorMap = {}, visibleIds = null, onToggle }) => {
	if (!partners.length) return null;

	const interactive = typeof onToggle === "function" && visibleIds != null;
	const allVisible = !interactive || partners.every((p) => visibleIds.has(p.id));

	return (
		<div
			style={{
				display: "flex",
				flexWrap: "wrap",
				gap: "10px",
				alignItems: "center",
				padding: "12px 16px",
				background: "var(--bg-secondary, #F9FAFB)",
				border: "1px solid var(--border-color, #E5E7EB)",
				borderRadius: "var(--radius-md, 8px)",
			}}
		>
			<span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.4, marginRight: 4 }}>
				Partners
			</span>

			{interactive && partners.length > 1 && (
				<button
					type="button"
					onClick={() => {
						if (allVisible) {
							// Keep at least the first partner visible so charts aren't empty.
							onToggle(new Set([partners[0].id]));
						} else {
							onToggle(new Set(partners.map((p) => p.id)));
						}
					}}
					style={{
						fontSize: 11,
						fontWeight: 700,
						padding: "4px 8px",
						borderRadius: 6,
						border: "1px solid var(--border-color, #E5E7EB)",
						background: "#fff",
						color: "var(--text-muted)",
						cursor: "pointer",
					}}
				>
					{allVisible ? "Hide all but first" : "Show all"}
				</button>
			)}

			{partners.map((p) => {
				const visible = !interactive || visibleIds.has(p.id);
				const color = colorMap[p.id] || "#9CA3AF";
				const Chip = interactive ? "button" : "div";
				return (
					<Chip
						key={p.id}
						type={interactive ? "button" : undefined}
						onClick={
							interactive
								? () => {
										const next = new Set(visibleIds);
										if (next.has(p.id)) {
											if (next.size <= 1) return; // always keep ≥1 partner
											next.delete(p.id);
										} else {
											next.add(p.id);
										}
										onToggle(next);
								  }
								: undefined
						}
						title={interactive ? (visible ? "Hide partner" : "Show partner") : undefined}
						style={{
							display: "inline-flex",
							alignItems: "center",
							gap: 6,
							padding: "4px 10px",
							borderRadius: 999,
							border: `1px solid ${visible ? color : "var(--border-color, #E5E7EB)"}`,
							background: visible ? `${color}14` : "#fff",
							opacity: visible ? 1 : 0.45,
							cursor: interactive ? "pointer" : "default",
							font: "inherit",
						}}
					>
						<span
							style={{
								width: 9,
								height: 9,
								borderRadius: "50%",
								background: color,
								display: "inline-block",
								flexShrink: 0,
							}}
						/>
						<span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>{p.name}</span>
					</Chip>
				);
			})}

			{interactive && partners.length > 1 && (
				<span style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: 4 }}>
					Click a partner to show/hide on charts
				</span>
			)}
		</div>
	);
};

export default PartnerLegend;
